import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { exportDdqDocument } from '../../../services/api';

interface ExportHistoryItem {
  document_id: string;
  ddq_name: string;
  format: string;
  status: string;
  file_path: string;
  id: string;
  exported_by_id: string;
  organization_id: string;
  created_at: string;
}

interface ExportHistoryTableProps {
  history: ExportHistoryItem[];
}

const ExportHistoryTable: React.FC<ExportHistoryTableProps> = ({ history }) => {
  const [loading, setLoading] = useState<string | null>(null);

  const getFormatLabel = (format: string) => {
    switch (format) {
      case 'word':
        return 'Word (.docx)';
      case 'xlsx':
        return 'Excel (.xlsx)';
      case 'pdf':
        return 'PDF (.pdf)';
      default:
        return format;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <Chip label="Completed" size="small" color="success" />;
      case 'failed':
        return <Chip label="Failed" size="small" color="error" />;
      case 'in_progress':
        return <Chip label="In Progress" size="small" color="primary" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleDownload = async (documentId: string, format: string, historyId: string) => {
    setLoading(historyId);
    try {
      await exportDdqDocument(documentId, format as 'docx' | 'xlsx' | 'pdf', historyId);
    } catch (error) {
      console.error('Failed to download export:', error);
    } finally {
      setLoading(null);
    }
  };

  if (history.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No recent exports found. Export your DDQ to see the history here.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 600 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>DDQ Name</TableCell>
            <TableCell>Exported By</TableCell>
            <TableCell>Format</TableCell>
            <TableCell>Date & Time</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((exportItem) => (
            <TableRow key={exportItem.id}>
              <TableCell>{exportItem.ddq_name}</TableCell>
              <TableCell>{exportItem.exported_by_id}</TableCell>
              <TableCell>{getFormatLabel(exportItem.format)}</TableCell>
              <TableCell>{new Date(exportItem.created_at).toLocaleString()}</TableCell>
              <TableCell>{getStatusChip(exportItem.status)}</TableCell>
              <TableCell align="right">
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(exportItem.document_id, exportItem.format, exportItem.id)}
                    disabled={loading !== null && loading !== exportItem.id}
                  >
                    {loading === exportItem.id ? <CircularProgress size={20} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExportHistoryTable;